import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Save, Plus, Trash2, Users, Clock, Award, ArrowLeft, Play, Sparkles, Loader2, X } from "lucide-react";

const DEFAULT_COLORS = ["#E5276A", "#1F3A5F", "#F9C548", "#4CAF50", "#8B0000", "#7C4DFF", "#FF6B35", "#00A896"];

export default function MaestroBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState({
    title: "", subtitle: "", default_timer_s: 30, default_points: 1000,
    teams: [{ name: "Rouge", color: "#E5276A" }, { name: "Bleu", color: "#1F3A5F" }],
    questions: [],
  });
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTheme, setAiTheme] = useState("");
  const [aiCount, setAiCount] = useState(6);
  const [aiDifficulty, setAiDifficulty] = useState("medium");

  useEffect(() => { if (id) load(); }, [id]);
  const load = async () => {
    try { const r = await api.get(`/maestro/quizzes/${id}`); setQuiz(r.data); }
    catch { toast.error("Quiz introuvable"); nav("/play/maestro"); }
  };

  const save = async (launchAfter = false) => {
    if (!quiz.title.trim()) return toast.error("Titre requis");
    if (quiz.questions.length === 0) return toast.error("Ajoutez au moins une question");
    if (quiz.questions.some(q => q.correct_idx == null || !q.question.trim())) return toast.error("Question incomplète");
    setSaving(true);
    try {
      const r = id ? await api.put(`/maestro/quizzes/${id}`, quiz) : await api.post("/maestro/quizzes", quiz);
      if (launchAfter) {
        const launch = await api.post("/maestro/rooms", { quiz_id: r.data.id });
        localStorage.setItem(`maestro_host_${launch.data.code}`, launch.data.host_token);
        nav(`/play/maestro/host/${launch.data.code}`);
      } else {
        toast.success("Enregistré");
        nav("/play/maestro");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const addTeam = () => setQuiz({ ...quiz, teams: [...quiz.teams, { name: `Équipe ${quiz.teams.length + 1}`, color: DEFAULT_COLORS[quiz.teams.length % DEFAULT_COLORS.length] }] });
  const rmTeam = (i) => setQuiz({ ...quiz, teams: quiz.teams.filter((_, k) => k !== i) });
  const addQuestion = () => setQuiz({ ...quiz, questions: [...quiz.questions, { question: "", choices: ["", "", "", ""], correct_idx: null, points: quiz.default_points, timer_s: quiz.default_timer_s }] });
  const rmQuestion = (i) => setQuiz({ ...quiz, questions: quiz.questions.filter((_, k) => k !== i) });
  const upQ = (i, patch) => setQuiz({ ...quiz, questions: quiz.questions.map((q, k) => k === i ? { ...q, ...patch } : q) });
  const upChoice = (qi, ci, val) => upQ(qi, { choices: quiz.questions[qi].choices.map((c, k) => k === ci ? val : c) });

  const AI_THEMES = [
    "Bourgogne", "Bordeaux", "Champagne & bulles", "Vallée du Rhône",
    "Loire", "Alsace", "Italie du Nord", "Espagne", "Nouveau Monde",
    "Cépages rouges", "Cépages blancs", "Accords mets & vins",
    "Vinification", "Terroirs & climats",
  ];

  const generateAI = async () => {
    if (!aiTheme.trim()) return toast.error("Choisissez un thème");
    setAiBusy(true);
    try {
      const r = await api.post("/maestro/generate-questions", {
        theme: aiTheme.trim(),
        count: aiCount,
        difficulty: aiDifficulty,
        language: "fr",
      });
      const generated = (r.data.questions || []).map((q) => ({
        question: q.question,
        choices: q.choices.length === 4 ? q.choices : [...q.choices, ...Array(4 - q.choices.length).fill("")],
        correct_idx: q.correct_idx,
        points: quiz.default_points,
        timer_s: quiz.default_timer_s,
      }));
      if (!generated.length) throw new Error("empty");
      setQuiz({ ...quiz, questions: [...quiz.questions, ...generated] });
      toast.success(`${generated.length} questions ajoutées`);
      setAiOpen(false);
      setAiTheme("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'IA n'a pas répondu, réessayez.");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10" data-testid="maestro-builder">
      <Toaster theme="light" />
      <button onClick={() => nav("/play/maestro")} className="inline-flex items-center gap-1 text-sm text-[#8B95A1] hover:text-[#E5276A] mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
      <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F]">
        {id ? "Éditer le quiz" : "Nouveau quiz Maestro"}
      </h1>

      {/* Global settings */}
      <div className="mt-6 rounded-2xl border border-[#1F3A5F]/10 bg-white p-5 space-y-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Titre du quiz</label>
          <input data-testid="maestro-title" value={quiz.title} onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none" placeholder="Ex : Soirée Bourgogne" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1] inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Timer (s)</label>
            <input type="number" min={5} max={120} value={quiz.default_timer_s}
              onChange={(e) => setQuiz({ ...quiz, default_timer_s: +e.target.value || 30 })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1] inline-flex items-center gap-1"><Award className="w-3 h-3" /> Points max</label>
            <input type="number" min={100} max={5000} step={100} value={quiz.default_points}
              onChange={(e) => setQuiz({ ...quiz, default_points: +e.target.value || 1000 })}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15" />
          </div>
        </div>
      </div>

      {/* Teams */}
      <div className="mt-6 rounded-2xl border border-[#1F3A5F]/10 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-[#E5276A]" /><h2 className="font-display text-lg text-[#1F3A5F]">Équipes</h2></div>
          <button onClick={addTeam} disabled={quiz.teams.length >= 8}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#FBF7F3] text-[#1F3A5F] hover:bg-[#FCE4ED] disabled:opacity-50">
            <Plus className="w-3 h-3" /> Ajouter
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="maestro-teams">
          {quiz.teams.map((t, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-[#1F3A5F]/10">
              <input type="color" value={t.color} onChange={(e) => setQuiz({ ...quiz, teams: quiz.teams.map((x, k) => k === i ? { ...x, color: e.target.value } : x) })}
                className="w-8 h-8 rounded cursor-pointer" />
              <input value={t.name} onChange={(e) => setQuiz({ ...quiz, teams: quiz.teams.map((x, k) => k === i ? { ...x, name: e.target.value } : x) })}
                className="flex-1 px-2 py-1 rounded border border-[#1F3A5F]/10 text-sm" placeholder="Nom d'équipe" />
              <button onClick={() => rmTeam(i)} disabled={quiz.teams.length <= 2}
                className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Questions */}
      <div className="mt-6 rounded-2xl border border-[#1F3A5F]/10 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-[#E5276A]" /><h2 className="font-display text-lg text-[#1F3A5F]">Questions ({quiz.questions.length})</h2></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAiOpen(true)} data-testid="maestro-ai-open"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-[#F9C548] to-[#E5276A] text-white hover:opacity-90">
              <Sparkles className="w-3 h-3" /> Générer avec l'IA
            </button>
            <button onClick={addQuestion} data-testid="maestro-add-question"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#1F3A5F] text-white hover:bg-[#2A4870]">
              <Plus className="w-3 h-3" /> Ajouter
            </button>
          </div>
        </div>
        <div className="space-y-4">
          {quiz.questions.map((q, qi) => (
            <div key={qi} className="p-4 rounded-xl bg-[#FBF7F3] border border-[#1F3A5F]/10" data-testid={`maestro-question-${qi}`}>
              <div className="flex items-start gap-2">
                <span className="text-xs font-bold text-[#E5276A] shrink-0 mt-2">Q{qi + 1}.</span>
                <input value={q.question} onChange={(e) => upQ(qi, { question: e.target.value })} placeholder="Votre question…"
                  className="flex-1 px-3 py-2 rounded-lg border border-[#1F3A5F]/15" />
                <button onClick={() => rmQuestion(qi)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {q.choices.map((c, ci) => (
                  <label key={ci} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${q.correct_idx === ci ? "border-[#4CAF50] bg-green-50" : "border-[#1F3A5F]/10 bg-white"}`}>
                    <input type="radio" name={`correct-${qi}`} checked={q.correct_idx === ci} onChange={() => upQ(qi, { correct_idx: ci })} className="accent-[#4CAF50]" />
                    <input value={c} onChange={(e) => upChoice(qi, ci, e.target.value)} placeholder={`Choix ${ci + 1}`} className="flex-1 bg-transparent outline-none text-sm" />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex gap-3 flex-wrap sticky bottom-4 z-10 bg-white/80 backdrop-blur p-3 rounded-2xl border border-[#1F3A5F]/10">
        <button onClick={() => save(false)} disabled={saving} data-testid="maestro-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#1F3A5F] text-[#1F3A5F] font-bold hover:bg-[#FBF7F3]">
          <Save className="w-4 h-4" /> {saving ? "…" : "Enregistrer"}
        </button>
        <button onClick={() => save(true)} disabled={saving} data-testid="maestro-save-launch"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] ml-auto">
          <Play className="w-4 h-4" /> Enregistrer & lancer
        </button>
      </div>

      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center md:p-4" data-testid="maestro-ai-modal"
          onClick={() => !aiBusy && setAiOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-t-3xl md:rounded-3xl bg-white shadow-2xl relative flex flex-col max-h-[92dvh] md:max-h-[85dvh]">
            <button onClick={() => setAiOpen(false)} disabled={aiBusy} aria-label="Fermer"
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-[#FBF7F3] hover:bg-[#FCE4ED] text-[#1F3A5F] hover:text-[#E5276A] flex items-center justify-center shadow-sm">
              <X className="w-5 h-5" />
            </button>
            <div className="overflow-y-auto p-6 md:p-8">
            <div className="inline-flex items-center gap-2 chip-pink">
              <Sparkles className="w-3 h-3" /> Génération IA
            </div>
            <h3 className="font-display text-2xl text-[#1F3A5F] mt-2">Créer des questions</h3>
            <p className="text-sm text-[#4A5568] mt-1">Claude va rédiger des QCM sur le thème de votre choix, ajoutés à la fin du quiz.</p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Thème</label>
                <input value={aiTheme} onChange={(e) => setAiTheme(e.target.value)} data-testid="maestro-ai-theme"
                  placeholder="Ex : Grands crus de Bourgogne"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none" />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {AI_THEMES.map((t) => (
                    <button key={t} onClick={() => setAiTheme(t)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${aiTheme === t ? "bg-[#E5276A] text-white border-[#E5276A]" : "border-[#1F3A5F]/15 text-[#4A5568] hover:border-[#E5276A]/50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Nombre</label>
                  <select value={aiCount} onChange={(e) => setAiCount(+e.target.value)} data-testid="maestro-ai-count"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 bg-white">
                    {[3, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n} questions</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">Difficulté</label>
                  <select value={aiDifficulty} onChange={(e) => setAiDifficulty(e.target.value)} data-testid="maestro-ai-difficulty"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 bg-white">
                    <option value="easy">Débutant</option>
                    <option value="medium">Amateur</option>
                    <option value="hard">Sommelier</option>
                  </select>
                </div>
              </div>
            </div>

            <button onClick={generateAI} disabled={aiBusy || !aiTheme.trim()} data-testid="maestro-ai-generate"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#F9C548] to-[#E5276A] text-white font-bold hover:opacity-90 disabled:opacity-50">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiBusy ? "Génération en cours…" : "Générer"}
            </button>
            <p className="text-[11px] text-[#8B95A1] mt-2 text-center">La génération prend 5 à 15 secondes.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
